import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import type {
  AiPromptAsset,
  PromptTarget,
  PromptTargetType,
  TargetPromptAiModel
} from '../model/types'
import { useAiApiCalls, useAiSignalREvents } from '../model/api'
import { useAppLoading } from '@gat-ship/vue-components'
import { LOADING_IDS } from '../constants'

export interface UsePromptAssetsStoreOptions {
  target: PromptTarget
  promptType: PromptTargetType
}

type PromptAssetsStoreState = ReturnType<typeof createPromptAssetsStoreState>

const promptAssetsStores = new Map<string, PromptAssetsStoreState>()

function getStoreKey(options: UsePromptAssetsStoreOptions) {
  return `${options.promptType}:${options.target}`
}

/**
 * Reactive prompt-asset state scoped to a single target.
 * Multiple callers for the same target share fetch and SignalR state.
 */
export function usePromptAssetsStore(options: UsePromptAssetsStoreOptions) {
  const key = getStoreKey(options)
  let state = promptAssetsStores.get(key)
  if (!state) {
    state = createPromptAssetsStoreState(options)
    promptAssetsStores.set(key, state)
  }
  const resolvedState = state

  onMounted(() => {
    resolvedState.mount()
  })

  onUnmounted(() => {
    resolvedState.unmount()
    if (resolvedState.mountedCount.value === 0) {
      promptAssetsStores.delete(key)
    }
  })

  return resolvedState
}

function createPromptAssetsStoreState(options: UsePromptAssetsStoreOptions) {
  const { target, promptType } = options

  const _assets = ref<AiPromptAsset[]>([])
  const virtualAssets = ref<AiPromptAsset[]>([])
  const selectedAssetId = ref<string | undefined>(undefined)
  const selectedThinkingModel = ref<TargetPromptAiModel | undefined>('standard')
  const mountedCount = ref(0)

  function getDefaultSystemPrompt(assets: AiPromptAsset[]): AiPromptAsset {
    return {
      id: target,
      promptType,
      promptTargetId: target,
      displayName: 'System Prompt',
      shortDescription: 'Base Instruction',
      targetAiModel: 'default',
      defaultModel: 'default',
      promptText: '',
      isDefault: !assets.some((a) => a.isDefault)
    }
  }

  function selectThinkingModelByAssetId(assetId: string) {
    const all = [..._assets.value, ...virtualAssets.value]
    const asset = all.find((a) => a.id === assetId)
    if (asset) {
      selectedThinkingModel.value = asset.targetAiModel
    }
  }

  watch(selectedAssetId, (assetId) => {
    if (assetId) {
      selectThinkingModelByAssetId(assetId)
    }
  })

  function setAssets(assets: AiPromptAsset[] = []) {
    const defaultSystemPrompt = getDefaultSystemPrompt(assets)
    const hasDefaultSystemPrompt = assets.some(
      (a) => a.id === defaultSystemPrompt.id
    )
    virtualAssets.value = hasDefaultSystemPrompt ? [] : [defaultSystemPrompt]
    _assets.value = assets

    const combinedAssets = [..._assets.value, ...virtualAssets.value]
    const defaultAssetId = combinedAssets.find((a) => a.isDefault)?.id
    if (defaultAssetId) {
      selectThinkingModelByAssetId(defaultAssetId)
    }
    selectedAssetId.value = defaultAssetId
  }

  const { getAssetsByTargetRequest } = useAiApiCalls()
  const { setLoadingId, removeLoadingId } = useAppLoading()

  async function fetchAssets() {
    try {
      setLoadingId(LOADING_IDS.assets)
      const response = await getAssetsByTargetRequest({ target })
      setAssets(response.assets)
    } finally {
      removeLoadingId(LOADING_IDS.assets)
    }
  }

  const signalR = useAiSignalREvents({
    onPromptAssetAdded: () => fetchAssets(),
    onPromptAssetUpdated: () => fetchAssets(),
    onPromptAssetRemoved: () => fetchAssets()
  })

  function mount() {
    mountedCount.value += 1
    if (mountedCount.value === 1) {
      fetchAssets()
      signalR.subscribeToPromptAssetEvents()
    }
  }

  function unmount() {
    mountedCount.value = Math.max(0, mountedCount.value - 1)
    if (mountedCount.value === 0) {
      signalR.unsubscribeFromPromptAssetEvents()
    }
  }

  const assets = computed(() => {
    return [..._assets.value, ...virtualAssets.value].sort((a, b) => {
      if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1
      const isSystemA = a.id === target
      const isSystemB = b.id === target
      if (isSystemA !== isSystemB) return isSystemA ? -1 : 1
      return a.displayName.localeCompare(b.displayName)
    })
  })

  function getCustomPromptAssetId(): string | undefined {
    const id = selectedAssetId.value
    if (!id) return undefined
    const isVirtual = virtualAssets.value.some((a) => a.id === id)
    return isVirtual ? undefined : id
  }

  function getAiModelOverride(
    hasPermission: boolean
  ): Omit<TargetPromptAiModel, 'default'> | undefined {
    if (!hasPermission) return undefined
    return selectedThinkingModel.value === 'default'
      ? undefined
      : selectedThinkingModel.value
  }

  return {
    assets,
    virtualAssets,
    selectedAssetId,
    selectedThinkingModel,
    mountedCount,
    mount,
    unmount,
    fetchAssets,
    getCustomPromptAssetId,
    getAiModelOverride,
    getDefaultSystemPrompt
  }
}
